import { keepPreviousData, useMutation, useQuery, UseQueryResult } from '@tanstack/react-query'
import { AuthStorage } from '@/utils/storage/AuthStorage'
import { callToCheckLoginUser, callToGetGridColumns, callToGetMenuList, callToSaveGridColumns, callToSignUp } from '@/services/auth/apis'
import { MenuStorage } from '@/utils/storage/MenuStorage'
import { MenuParentModel } from '@/services/auth/menu.types'
import { GridColumnState } from '@/services/auth/gridColumn.types'

const PATH = ['auth']
const GET_MENU_LIST = [...PATH, 'GET_MENU_LIST']

export const gridColumnsQueryKeys = {
  all: [...PATH, 'GRID_COLUMNS'] as const,
  detail: (gridKey: string, shipperCode: string) => [...gridColumnsQueryKeys.all, gridKey, shipperCode] as const,
}

export const useCheckUser = () => {
  return useMutation({
    mutationFn: () => callToCheckLoginUser(),
  })
}

export const useSignUp = () => {
  return useMutation({
    mutationFn: callToSignUp,
  })
}

export const useGetMenuList = (): UseQueryResult<MenuParentModel[], Error> => {
  return useQuery({
    queryKey: GET_MENU_LIST,
    enabled: false,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const userInfo = AuthStorage.user.get()
      const { AUTH_CD = '', company = '', email = '' } = userInfo

      const menuList = await callToGetMenuList({ AUTH_CD, SPR_NM: company, email })
      MenuStorage.menu.set(menuList)

      return menuList
    },
    select: (data) =>
      data.map((parent) => ({
        menuCode: parent.MENU_CD.toLowerCase(),
        menuName: parent.MENU_NM,
        menuItem:
          parent.MENU_ITEM?.map((child) => ({
            menuCode: child.MENU_CD.toLowerCase(),
            menuName: child.MENU_NM,
            link: `/${parent.MENU_CD.toLowerCase()}/${child.MENU_CD.toLowerCase()}`,
            allowedButtons: child.allowedButtons,
            toolbar: child.toolbar,
            editable: child.editable,
          })) ?? [],
      })),
    meta: {
      noErrorMessage: true,
    },
  })
}

// LOGISPRD-15456 — 저장값이 없고 legacyColumns(GI23~26 예전 localStorage 배치)가 있으면 그걸 이 화주의 저장값으로 올리고 쓴다.
// 조회·저장 실패는 화면을 막지 않는다 — 기본 배치로 그린다.
export const useGetGridColumns = (gridKey: string | undefined, shipperCode: string, legacyColumns: GridColumnState[]): UseQueryResult<GridColumnState[], Error> => {
  return useQuery({
    // legacyColumns 는 queryKey 에서 뺀다 — 이 코드가 쓰지도 지우지도 않는 localStorage 값이라 캐시가 지워져 다시 조회해도 같은 값이고,
    // useTableColumnDefs 의 setQueryData 가 같은 키(gridKey·화주)로 저장값을 갱신해야 한다.
    queryKey: gridColumnsQueryKeys.detail(gridKey ?? '', shipperCode),
    enabled: !!gridKey && !!shipperCode,
    staleTime: Infinity,
    queryFn: async () => {
      const items = await callToGetGridColumns(gridKey ?? '', shipperCode)
      if (items.length > 0 || legacyColumns.length === 0) return items

      callToSaveGridColumns(gridKey ?? '', shipperCode, legacyColumns).catch(() => undefined)
      return legacyColumns
    },
    meta: {
      noErrorMessage: true,
    },
  })
}
